import { Controller, Form } from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { paths } from '@common/types';
import useLicenses from './hooks/useLicenses.ts';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Select } from 'ui';
import { SaveChangesModal } from '@components';

interface ILicensesProps {
  provider: paths[`/provider/{record_id}`]['get']['responses']['200']['content']['application/json'];
}

const Licenses = ({ provider }: ILicensesProps) => {
  const {
    control,
    onSubmit,
    isSubmitEnabled,
    isSubmitting,
    watch,
    handleAddLicense,
    licensesFields,
    handleRemoveLicense,
    licenseOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useLicenses(provider);
  const isGuideCertified = watch('guide_certified');

  return (
    <Form
      control={control}
      onSubmit={e => onSubmit(e.event)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: 'fit-content',
        borderRadius: 0,
        backgroundColor: 'white',
        position: 'relative',
        gap: '1rem',
      }}
    >
      <Controller
        name='npi'
        control={control}
        disabled={provider.inactive}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            size='medium'
            label={t('adminPanel.providers.addModal.licenses.npi')}
            variant='outlined'
            error={!isNil(error)}
            helperText={error ? error.message : null}
            required
            fullWidth
          />
        )}
      />

      <Divider style={{ width: '100%' }} />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'row',
          gap: '0.5rem',
        }}
      >
        <Controller
          control={control}
          name='guide_certified'
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={provider.inactive}
                  checked={value}
                  onChange={onChange}
                />
              }
              label={t('adminPanel.providers.addModal.licenses.guideCertified')}
            />
          )}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Controller
            control={control}
            name='guide_certification_date'
            rules={{ required: true }}
            render={({ field }) => {
              return (
                <DatePicker
                  disabled={!isGuideCertified || provider.inactive}
                  label={t(
                    'adminPanel.providers.addModal.licenses.certificationDate',
                  )}
                  name={field.name}
                  value={field.value}
                  inputRef={field.ref}
                  onChange={date => {
                    field.onChange(dayjs(date));
                  }}
                />
              );
            }}
          />
        </LocalizationProvider>
      </Box>

      <Divider style={{ width: '100%' }} />

      <Typography
        variant='subtitle2'
        sx={{
          width: '100%',
          textAlign: 'left',
        }}
      >
        {t('adminPanel.providers.addModal.licenses.otherSection')}
      </Typography>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          gap: '1rem',
          flexDirection: 'column',
        }}
      >
        {licensesFields.map(({ id }, index) => {
          return (
            <Box
              key={`license-${id}-${index}`}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  gap: '0.5rem',
                }}
              >
                <Controller
                  name={`licenses.${index}.type`}
                  control={control}
                  disabled={provider.inactive}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      size='medium'
                      label={t(
                        'adminPanel.providers.addModal.licenses.licenseType',
                      )}
                      error={!isNil(error)}
                      required
                      options={licenseOptions}
                      helperText={error?.message}
                    />
                  )}
                />

                <Controller
                  name={`licenses.${index}.value`}
                  control={control}
                  disabled={provider.inactive}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth>
                      <TextField
                        {...field}
                        size='medium'
                        variant='outlined'
                        label={t(
                          'adminPanel.providers.addModal.licenses.licenseId',
                        )}
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        required
                        fullWidth
                      />

                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
                <Box sx={{ width: '5%' }}>
                  <Button
                    disabled={provider.inactive}
                    startIcon={<DeleteIcon />}
                    sx={{
                      '& .MuiButton-startIcon': {
                        margin: 0,
                      },
                      mt: '1rem',
                      minWidth: '5px !important',
                    }}
                    size='small'
                    color='error'
                    variant='text'
                    onClick={() => handleRemoveLicense(index)}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
        <Box sx={{ width: '100%' }}>
          <Button
            startIcon={<AddIcon />}
            disabled={provider.inactive}
            sx={{
              fontWeight: 'regular',
              fontSize: '1rem',
              color: 'primary.main',
            }}
            color='secondary'
            variant='text'
            onClick={handleAddLicense}
          >
            {t('adminPanel.providers.addModal.licenses.addLicense')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          pt: '0.5rem',
          position: 'sticky',
          bottom: '0',
          left: 0,
          bgcolor: theme => theme.palette.common.white,
          zIndex: 10,
        }}
      >
        <Button
          type='submit'
          variant='contained'
          color='secondary'
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
            color: 'white',
          }}
          disabled={!isSubmitEnabled || provider.inactive}
          loading={isSubmitting}
          loadingPosition='center'
        >
          {t('general.saveChanges')}
        </Button>
      </Box>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Form>
  );
};

export default Licenses;
